import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useRef, useState } from 'react';
import '../styles/radioButton.scss';
import ButtonComponent from './ButtonComponent';
var RadioButton = function (_a) {
    var _b;
    var id = _a.id, options = _a.options, _c = _a.selected, selected = _c === void 0 ? -1 : _c, _d = _a.onChange, onChange = _d === void 0 ? function () { } : _d, _e = _a.disabled, disabled = _e === void 0 ? false : _e, _f = _a.toggle, toggle = _f === void 0 ? false : _f, _g = _a.className, className = _g === void 0 ? '' : _g, name = _a.name, _h = _a.optionsType, optionsType = _h === void 0 ? 'text' : _h, imgWidth = _a.imgWidth, imgHeight = _a.imgHeight, _j = _a.type, type = _j === void 0 ? 'blue' : _j, _k = _a.disabledOptions, disabledOptions = _k === void 0 ? [] : _k, _l = _a.nonTabbableOptions, nonTabbableOptions = _l === void 0 ? [] : _l, tooltips = _a.tooltips, ariaLabels = _a.ariaLabels, _m = _a.useAriaLabelFromOutside, useAriaLabelFromOutside = _m === void 0 ? false : _m, groupAriaLabel = _a.groupAriaLabel, groupAriaLabelledBy = _a.groupAriaLabelledBy;
    var groupName = name || id;
    var _o = useState(selected !== null && selected !== void 0 ? selected : -1), internalSelected = _o[0], setInternalSelected = _o[1];
    var _p = useState(-1), focusedIndex = _p[0], setFocusedIndex = _p[1];
    var inputsRef = useRef([]);
    // sync controlled -> internal
    useEffect(function () { return setInternalSelected(selected !== null && selected !== void 0 ? selected : -1); }, [selected]);
    // stable ref setter (returns void to satisfy TS callback ref type)
    var setInputRef = useCallback(function (el, index) {
        inputsRef.current[index] = el;
    }, []);
    // call when selecting (from click or keyboard)
    var handleSelect = useCallback(function (index) {
        if (disabled || disabledOptions.includes(index))
            return;
        if (toggle && internalSelected === index) {
            setInternalSelected(-1);
            onChange(-1);
        }
        else {
            setInternalSelected(index);
            onChange(index);
        }
        // after selecting, ensure the selected input is tabbable and focused
        window.requestAnimationFrame(function () {
            var _a;
            (_a = inputsRef.current[index]) === null || _a === void 0 ? void 0 : _a.focus();
        });
    }, [disabled, internalSelected, onChange, toggle]);
    // Keyboard navigation: focus moves and selection on Space/Enter
    var handleKeyDown = useCallback(function (e, idx) {
        var _a, _b;
        if (disabled)
            return;
        var isSkippable = function (i) {
            return disabledOptions.includes(i) || nonTabbableOptions.includes(i);
        };
        var key = e.key;
        if (key === 'ArrowDown' || key === 'ArrowRight') {
            e.preventDefault();
            var next = (idx + 1) % options.length;
            var guard = 0;
            // Skip disabled / non-tabbable options so arrows never land on them.
            while (isSkippable(next) && guard < options.length) {
                next = (next + 1) % options.length;
                guard++;
            }
            if (guard >= options.length)
                return; // no eligible option
            (_a = inputsRef.current[next]) === null || _a === void 0 ? void 0 : _a.focus();
            setFocusedIndex(next);
        }
        else if (key === 'ArrowUp' || key === 'ArrowLeft') {
            e.preventDefault();
            var prev = (idx - 1 + options.length) % options.length;
            var guard = 0;
            while (isSkippable(prev) && guard < options.length) {
                prev = (prev - 1 + options.length) % options.length;
                guard++;
            }
            if (guard >= options.length)
                return;
            (_b = inputsRef.current[prev]) === null || _b === void 0 ? void 0 : _b.focus();
            setFocusedIndex(prev);
        }
        else if (key === 'Escape') {
            e.stopPropagation();
            e.currentTarget.blur();
        }
        else if (key === ' ' || key === 'Spacebar' || key === 'Enter') {
            e.preventDefault();
            handleSelect(idx);
        }
    }, [disabled, disabledOptions, nonTabbableOptions, handleSelect, options.length]);
    // compute tabIndex for each radio: selected -> 0, if none selected -> first -> 0, others -1.
    // nonTabbableOptions always yield -1 (even when selected), so Tab skips them.
    var getTabIndex = useCallback(function (idx) {
        if (disabled || disabledOptions.includes(idx))
            return -1;
        if (nonTabbableOptions.includes(idx))
            return -1;
        var selectedIsTabbable = internalSelected >= 0 && !nonTabbableOptions.includes(internalSelected);
        if (selectedIsTabbable)
            return internalSelected === idx ? 0 : -1;
        // Either nothing is selected, or the selected option is non-tabbable.
        // Make the first keyboard-eligible option the sole tab stop.
        var firstEligible = options.findIndex(function (_, i) { return !disabledOptions.includes(i) && !nonTabbableOptions.includes(i); });
        return idx === firstEligible ? 0 : -1;
    }, [disabled, disabledOptions, nonTabbableOptions, internalSelected, options]);
    return (_jsx("div", { id: id, role: "radiogroup", "aria-disabled": disabled, "aria-label": groupAriaLabelledBy ? undefined : groupAriaLabel, "aria-labelledby": groupAriaLabelledBy, className: "customRadioGroup ".concat(className), style: (_b = {},
            _b["--radio-Color"] = type === 'white' ? 'white' : '#293273',
            _b), "data-disabled": disabled ? 'true' : 'false', children: options.map(function (opt, idx) {
            var _a;
            var isOptionDisabled = disabled || disabledOptions.includes(idx);
            var isSelected = internalSelected === idx;
            var tabIndexValue = getTabIndex(idx);
            return (_jsxs("div", { className: "customRadioItem", "data-selected": isSelected ? 'true' : 'false', "data-focused": focusedIndex === idx ? 'true' : 'false', "data-disabled": isOptionDisabled ? 'true' : 'false', children: [_jsx("input", { type: "radio", id: "".concat(id, "_option_").concat(idx), name: groupName, "aria-label": useAriaLabelFromOutside && (ariaLabels === null || ariaLabels === void 0 ? void 0 : ariaLabels[idx])
                            ? ariaLabels[idx]
                            : optionsType === 'image'
                                ? "Option ".concat(idx + 1)
                                : opt, "aria-checked": isSelected, "aria-disabled": isOptionDisabled, tabIndex: tabIndexValue, checked: isSelected, disabled: isOptionDisabled, className: "customRadioInput", value: String(idx), ref: function (el) { return setInputRef(el, idx); }, onChange: function () { return handleSelect(idx); }, onKeyDown: function (e) { return handleKeyDown(e, idx); }, onFocus: function () { return setFocusedIndex(idx); }, onBlur: function () { return setFocusedIndex(-1); } }), _jsxs("span", { className: "customRadioLabel", "aria-hidden": "true", onClick: function () { return handleSelect(idx); }, onMouseEnter: function () { return setFocusedIndex(idx); }, onMouseLeave: function () { return setFocusedIndex(-1); }, children: [(tooltips === null || tooltips === void 0 ? void 0 : tooltips[idx]) ? (_jsx(ButtonComponent, { avoidDefaultStyle: true, showToolTip: true, title: tooltips[idx].text, direction: (_a = tooltips[idx].direction) !== null && _a !== void 0 ? _a : 'top', isParentRelative: true, forceHovered: focusedIndex === idx, tabIndex: -1, ariaHidden: true, normalCSS: "radioVisualBtn", children: _jsx("span", { className: "radioVisual", children: _jsx("span", { className: "radioInner" }) }) })) : (_jsx("span", { className: "radioVisual", children: _jsx("span", { className: "radioInner" }) })), optionsType === 'text' ? (_jsx("span", { className: "radioText", children: opt })) : (_jsx("span", { style: { pointerEvents: 'none' }, children: _jsx("img", { alt: "", src: opt, style: { pointerEvents: 'none' }, width: imgWidth ? imgWidth : 'auto', height: imgHeight ? imgHeight : '100%' }) }))] })] }, "".concat(id, "_option_").concat(idx)));
        }) }));
};
export default RadioButton;
